import React from 'react';
import { Shield, Lock, Eye, FileText } from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const PrivacyPolicy: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#faf8ed]">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 md:pt-52 pb-20 px-4 bg-mesh-premium relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 bg-white/20 backdrop-blur-3xl"></div>
        <div className={`container-premium text-center relative z-10 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
          <div className="flex justify-center mb-8">
            <div className="bg-[#0c513f] p-6 rounded-[1.5rem] shadow-xl shadow-[#0c513f]/20">
              <Shield className="text-white" size={48} />
            </div>
          </div>
          <h1 className="heading-huge text-[#0c513f] mb-6 tracking-tighter-premium">
            Privacy Policy
          </h1>
          <p className="text-2xl text-secondary-600 max-w-3xl mx-auto leading-tight font-medium">
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
          </p>
          <p className="text-sm font-bold text-[#0c513f]/60 mt-6 uppercase tracking-widest">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section ref={contentRef} className="py-20 px-4">
        <div className={`container-premium max-w-5xl bg-white rounded-[3rem] p-6 md:p-16 shadow-2xl shadow-[#0c513f]/5 border border-[#0c513f]/5 relative ${contentVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
          <div className="space-y-16 text-secondary-700">
            {/* Introduction */}
            <div>
              <h2 className="text-4xl font-black text-[#0c513f] mb-6 flex items-center gap-4">
                <div className="bg-[#faf8ed] p-2 rounded-xl">
                  <FileText className="text-[#0c513f]" size={32} />
                </div>
                1. Introduction
              </h2>
              <p className="text-xl leading-relaxed font-medium">
                Rita ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect,
                use, disclose, and safeguard your information when you use our QR payment and discovery platform, including our
                website, mobile application, and related services (collectively, the "Service").
              </p>
              <p className="text-xl leading-relaxed font-medium mt-4">
                By using our Service, you agree to the collection and use of information in accordance with this policy. If you
                do not agree with our policies and practices, please do not use our Service.
              </p>
            </div>

            {/* Information We Collect */}
            <div>
              <h2 className="text-4xl font-black text-[#0c513f] mb-6 flex items-center gap-4">
                <div className="bg-[#faf8ed] p-2 rounded-xl">
                  <Eye className="text-[#0c513f]" size={32} />
                </div>
                2. Information We Collect
              </h2>

              <h3 className="text-2xl font-bold text-[#0c513f] mb-4 mt-8">
                2.1 Personal Information
              </h3>
              <p className="text-xl leading-relaxed mb-6 font-medium">
                We may collect the following types of personal information:
              </p>
              <ul className="space-y-4 text-lg leading-relaxed font-medium">
                {[
                  { title: 'Account Information', desc: 'Name, email address, phone number, date of birth, and profile picture' },
                  { title: 'Payment Information', desc: 'Bank account details, payment card information, and transaction history' },
                  { title: 'Identity Verification', desc: 'Government-issued ID, proof of address, and KYC documents' },
                  { title: 'Location Data', desc: 'GPS coordinates, IP address, and location-based preferences' },
                  { title: 'Device Information', desc: 'Device type, operating system, unique device identifiers, and mobile network information' },
                  { title: 'Usage Data', desc: 'How you interact with our Service, pages visited, features used, and time spent' }
                ].map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="w-2 h-2 rounded-full bg-[#0c513f] mt-2.5 flex-shrink-0"></span>
                    <span><strong className="text-[#0c513f]">{item.title}:</strong> {item.desc}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-2xl font-bold text-[#0c513f] mb-4 mt-8">
                2.2 Automatically Collected Information
              </h3>
              <p className="text-xl leading-relaxed font-medium mb-4">
                When you use our Service, we automatically collect certain information, including:
              </p>
              <ul className="space-y-3 text-lg leading-relaxed font-medium pl-2">
                {['Log data (IP address, browser type, access times, pages viewed)', 'Cookies and similar tracking technologies', 'Device information and identifiers', 'Location information (with your consent)'].map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="w-2 h-2 rounded-full bg-[#0c513f] mt-2.5 flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h2 className="text-4xl font-black text-[#0c513f] mb-6 flex items-center gap-4">
                <div className="bg-[#faf8ed] p-2 rounded-xl">
                  <Lock className="text-[#0c513f]" size={32} />
                </div>
                3. How We Use Your Information
              </h2>
              <p className="text-xl leading-relaxed mb-6 font-medium">
                We use the information we collect for the following purposes:
              </p>
              <ul className="grid md:grid-cols-2 gap-4 text-lg leading-relaxed font-medium">
                {[
                  { t: 'Service Provision', d: 'To provide, maintain, and improve our services' },
                  { t: 'Transaction Processing', d: 'To process payments and verify transactions' },
                  { t: 'Business Discovery', d: 'To help you discover nearby businesses' },
                  { t: 'Security', d: 'To verify identity and prevent fraud' },
                  { t: 'Communication', d: 'To send updates and notifications' },
                  { t: 'Personalization', d: 'To customize your experience' },
                  { t: 'Analytics', d: 'To analyze usage patterns' },
                  { t: 'Legal Compliance', d: 'To comply with applicable laws' }
                ].map((item, i) => (
                  <li key={i} className="bg-[#faf8ed] p-4 rounded-xl border border-[#0c513f]/5">
                    <strong className="text-[#0c513f] block mb-1">{item.t}</strong>
                    <span className="text-secondary-600 text-sm">{item.d}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Information Sharing */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                4. Information Sharing and Disclosure
              </h2>
              <p className="text-xl leading-relaxed mb-6 font-medium">
                We do not sell your personal information. We may share your information in the following circumstances:
              </p>
              <ul className="space-y-4 text-lg leading-relaxed font-medium">
                {[
                  { t: 'Service Providers', d: 'With third-party service providers who perform services on our behalf (payment processors, cloud storage, analytics)' },
                  { t: 'Business Partners', d: 'With verified businesses on our platform to facilitate transactions and services' },
                  { t: 'Legal Requirements', d: 'When required by law, court order, or government regulation' },
                  { t: 'Business Transfers', d: 'In connection with a merger, acquisition, or sale of assets' },
                  { t: 'With Your Consent', d: 'When you explicitly authorize us to share your information' }
                ].map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="w-2 h-2 rounded-full bg-[#0c513f] mt-2.5 flex-shrink-0"></span>
                    <span><strong className="text-[#0c513f]">{item.t}:</strong> {item.d}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                5. Data Security
              </h2>
              <p className="text-xl leading-relaxed font-medium mb-6">
                We implement industry-standard security measures to protect your personal information, including:
              </p>
              <div className="bg-[#faf8ed] p-6 md:p-8 rounded-[2rem] border border-[#0c513f]/10">
                <ul className="space-y-3 text-lg font-bold text-[#0c513f]">
                  {['End-to-end encryption for sensitive data', 'Secure storage with encryption at rest', 'Regular security audits', 'Access controls and authentication', 'Compliance with PCI DSS standards'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Shield size={20} className="fill-[#0c513f]/20" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-lg leading-relaxed mt-6 font-medium text-secondary-500">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to
                use commercially acceptable means to protect your information, we cannot guarantee absolute security.
              </p>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                6. Your Privacy Rights
              </h2>
              <p className="text-lg leading-relaxed mb-6 font-medium">
                Depending on your location, you may have rights regarding your personal information such as Access, Correction, Deletion, Portability, Opt-Out, and Withdrawal of Consent.
              </p>
              <p className="text-lg leading-relaxed mt-4 font-bold text-[#0c513f]">
                To exercise these rights, please contact us at <a href="mailto:info@rita.ng" className="underline decoration-2 underline-offset-4 hover:opacity-80">info@rita.ng</a>.
              </p>
            </div>

            {/* Cookies & Children */}
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-[#0c513f] mb-4">
                  7. Cookies
                </h2>
                <p className="text-lg leading-relaxed font-medium">
                  We use cookies and similar tracking technologies to track activity on our Service and store certain information.
                  You can instruct your browser to refuse all cookies.
                </p>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#0c513f] mb-4">
                  8. Children's Privacy
                </h2>
                <p className="text-lg leading-relaxed font-medium">
                  Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information
                  from children.
                </p>
              </div>
            </div>

            {/* Changes to Policy */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-4">
                9. Changes to This Privacy Policy
              </h2>
              <p className="text-lg leading-relaxed font-medium">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy
                Policy on this page and updating the "Last updated" date.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-[#0c513f] p-8 md:p-10 rounded-[2.5rem] text-white text-center">
              <h2 className="text-3xl font-black mb-6">
                10. Contact Us
              </h2>
              <p className="text-xl leading-relaxed font-medium mb-6 opacity-90">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <a href="mailto:info@rita.ng" className="inline-block bg-[#faf8ed] text-[#0c513f] px-8 py-4 rounded-full font-black text-xl hover:scale-105 transition-transform">
                info@rita.ng
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

